import { useQuery } from '@tanstack/react-query';

import type { BackendTypes, GetResponse } from '@tf/api';
import { BackendClient, GraphQLClient, gql } from '@tf/api';
import type { IdenfyCheckResult } from '@tf/shared';
import { T } from '@tf/utils';

import { getObjectUriByNodeId } from '@/core/api/legacy';

export type LivenessCheck = {
	id: string;
	meta: {
		service_kind: 'idenfy';
		state: 'COMPLETED' | 'FAILED' | 'LIMITED' | 'NEW';
	};
	data: {
		web_token: string;
		overall?: IdenfyCheckResult;
	};
};

const getUnitLivenessCheck = async (unitObjectId: string): Promise<LivenessCheck | null> => {
	const res = await GraphQLClient.getInstance().request<GetResponse<LivenessCheck>, { unitObjectId: string }>(
		gql`
			query UnitLivenessCheck($unitObjectId: JSON!) {
				get(
					rootFilter: {
						kind: "liveness_check"
						meta: { AND: [{ key: "/@with/unit", operator: EQ, value: $unitObjectId }] }
					}
					paginationFilter: { limit: 1, offset: 0, sortByMeta: "/@createdAt", sortByDirection: DESC }
				) {
					id
					meta(path: ["/@version", "/service_kind", "/state"])
					data(path: ["/web_token", "/response/status/overall"])
				}
			}
		`,
		{ unitObjectId },
	);
	return res.get[0] ?? null;
};

export const useApplicationLivenessCheck = (unitNodeId: BackendTypes.GraphId) => {
	return useQuery({
		queryKey: ['applicationLivenessCheck', unitNodeId],
		queryFn: async (): Promise<LivenessCheck> => {
			const unitObjectId = await getObjectUriByNodeId(unitNodeId);

			// Try to get existing check first
			const existingCheck = await getUnitLivenessCheck(unitObjectId);
			if (existingCheck) {
				return existingCheck;
			}

			// If there is no check, create it
			await BackendClient.getInstance().createLivenessCheck({
				serviceKind: 'idenfy',
				objectId: unitObjectId,
			});

			const newCheck = await getUnitLivenessCheck(unitObjectId);
			T.assertNotNullish(newCheck, "couldn't get liveness check after creation");

			return newCheck;
		},
		// Completely disable cache
		staleTime: 0,
		gcTime: 0,
	});
};
